import { useEffect, useRef, useState } from "react";
import { Loader2, ShieldCheck, ShieldAlert, FileImage, RefreshCw, Activity, IndianRupee, Users, AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import AdminTutorPermanentDelete from "./AdminTutorPermanentDelete";

/**
 * V25 Batch B #15 — Admin Tutor Detail panel.
 *
 * Embedded inside LeadDetailModal (kind="tutor-applications") to surface:
 *  - Aadhaar verification toggle (with image preview)
 *  - Lifetime stats (applied leads, assignments, ongoing tuitions)
 *  - Recent login history (latest 20 attempts)
 *  - Manual referral note (Refer & Earn is kept manual per Batch A)
 */
export default function AdminTutorDetailPanel({ applicationId, onAadhaarChanged, onClose, onDeleted }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [verifyBusy, setVerifyBusy] = useState(false);
    const [verifyNote, setVerifyNote] = useState("");
    const [referralNote, setReferralNote] = useState("");
    const [savingReferral, setSavingReferral] = useState(false);
    const [aadhaarImgs, setAadhaarImgs] = useState({ front: null, back: null });
    const aadhaarImgsRef = useRef({ front: null, back: null });
    const [aadhaarImgLoading, setAadhaarImgLoading] = useState({ front: false, back: false });
    const [aadhaarImgErrors, setAadhaarImgErrors] = useState({ front: null, back: null });

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/admin/tutor-applications/${applicationId}/full`);
            setData(res.data);
            setVerifyNote(res.data?.aadhaar?.verification_note || "");
            setReferralNote(res.data?.referral_note || "");
            const a = res.data?.aadhaar || {};
            if (a.front_uploaded) loadAadhaarImage("front");
            if (a.back_uploaded) loadAadhaarImage("back");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load tutor detail");
        } finally {
            setLoading(false);
        }
    };

    // Fetch Aadhaar images as blobs (auth-protected endpoint), turn into object URLs
    const loadAadhaarImage = async (side) => {
        setAadhaarImgLoading((s) => ({ ...s, [side]: true }));
        setAadhaarImgErrors((s) => ({ ...s, [side]: null }));
        try {
            const res = await api.get(
                `/admin/tutor-applications/${applicationId}/aadhaar/${side}`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(res.data);
            const prevUrl = aadhaarImgsRef.current[side];
            if (prevUrl) URL.revokeObjectURL(prevUrl);
            aadhaarImgsRef.current = { ...aadhaarImgsRef.current, [side]: url };
            setAadhaarImgs((prev) => ({ ...prev, [side]: url }));
        } catch (err) {
            const detail = err.response?.status === 404
                ? "Image not uploaded"
                : (err.response?.data?.detail || err.message || "Failed to load image");
            setAadhaarImgErrors((s) => ({ ...s, [side]: detail }));
            const prevUrl = aadhaarImgsRef.current[side];
            if (prevUrl) URL.revokeObjectURL(prevUrl);
            aadhaarImgsRef.current = { ...aadhaarImgsRef.current, [side]: null };
            setAadhaarImgs((prev) => ({ ...prev, [side]: null }));
        } finally {
            setAadhaarImgLoading((s) => ({ ...s, [side]: false }));
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId]);

    // Best-effort cleanup of object URLs on unmount to avoid memory leaks.
    // Browser will also reclaim them on page navigation.
    useEffect(() => () => revokeCachedAadhaarUrls(aadhaarImgsRef), []);

    const toggleVerify = async (newValue) => {
        setVerifyBusy(true);
        try {
            await api.post(`/admin/tutor-applications/${applicationId}/aadhaar/verify`, {
                verified: newValue,
                note: verifyNote || null,
            });
            toast.success(newValue ? "Aadhaar marked verified" : "Aadhaar verification removed");
            await fetchData();
            onAadhaarChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update Aadhaar verification");
        } finally {
            setVerifyBusy(false);
        }
    };

    const saveReferralNote = async () => {
        setSavingReferral(true);
        try {
            await api.patch(`/admin/tutor-applications/${applicationId}/referral-note`, {
                note: referralNote,
            });
            toast.success("Referral note saved");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save referral note");
        } finally {
            setSavingReferral(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-3" data-testid="admin-tutor-detail-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading tutor detail…
            </div>
        );
    }
    if (!data) return null;

    const aadhaar = data.aadhaar || {};
    const stats = data.stats || {};
    const account = data.account;
    const history = data.login_history || [];

    return (
        <div className="space-y-4" data-testid="admin-tutor-detail-panel">
            {/* V30 — Service Area: show "Online - All Locations" for online-only
                tutors, the PIN list for Home / Both modes. */}
            <ServiceAreaBlock application={data.application || {}} />

            {/* Aadhaar verification */}
            <div className="rounded-xl border-2 border-amber-200 bg-amber-50/50 p-4 space-y-3" data-testid="admin-tutor-aadhaar-block">
                <div className="flex items-start gap-2">
                    {aadhaar.verified
                        ? <ShieldCheck className="w-5 h-5 text-emerald-700" />
                        : <ShieldAlert className="w-5 h-5 text-amber-700" />}
                    <div className="flex-1">
                        <div className="text-sm font-bold text-[#0F172A] inline-flex items-center gap-2">
                            Aadhaar:
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${
                                aadhaar.verified ? "bg-emerald-100 border-emerald-200 text-emerald-800"
                                                 : "bg-amber-100 border-amber-200 text-amber-900"
                            }`} data-testid="admin-tutor-aadhaar-status">
                                {aadhaar.verified ? "Verified" : "Pending"}
                            </span>
                        </div>
                        <div className="text-[11px] text-slate-700 mt-0.5">
                            Number: <span className="font-mono">{aadhaar.number || "—"}</span>
                            {aadhaar.verified_at && (
                                <> · Verified {new Date(aadhaar.verified_at).toLocaleString()} by {aadhaar.verified_by}</>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => { fetchData(); }}
                        className="text-xs text-slate-500 hover:text-[#1E3A8A] inline-flex items-center gap-1"
                        data-testid="admin-tutor-aadhaar-refresh"
                    >
                        <RefreshCw className="w-3 h-3" /> Refresh
                    </button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    {["front", "back"].map((side) => {
                        const uploaded = !!aadhaar[`${side}_uploaded`];
                        const blobUrl = aadhaarImgs[side];
                        const isLoading = aadhaarImgLoading[side];
                        const imgErr = aadhaarImgErrors[side];
                        return (
                            <div key={side} className="border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid={`admin-aadhaar-${side}-card`}>
                                <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider px-2 py-1 border-b border-slate-100 inline-flex items-center gap-1">
                                    <FileImage className="w-3 h-3" /> Aadhaar — {side}
                                </div>
                                {!uploaded ? (
                                    <div className="text-xs text-slate-400 italic px-3 py-6 text-center">Not uploaded</div>
                                ) : isLoading ? (
                                    <div className="flex items-center justify-center gap-2 px-3 py-8 text-xs text-slate-500" data-testid={`admin-aadhaar-${side}-loading`}>
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                                    </div>
                                ) : imgErr ? (
                                    <div className="px-3 py-6 text-center" data-testid={`admin-aadhaar-${side}-error`}>
                                        <div className="text-xs text-red-600 mb-1.5">{imgErr}</div>
                                        <button
                                            type="button"
                                            onClick={() => loadAadhaarImage(side)}
                                            className="text-[11px] underline text-[#1E3A8A] hover:text-[#1E40AF]"
                                            data-testid={`admin-aadhaar-${side}-retry`}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                ) : blobUrl ? (
                                    <a
                                        href={blobUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`admin-aadhaar-${side}-link`}
                                    >
                                        <img
                                            src={blobUrl}
                                            alt={`Aadhaar ${side}`}
                                            className="w-full max-h-48 object-contain bg-slate-50 cursor-zoom-in"
                                            data-testid={`admin-aadhaar-${side}-img`}
                                        />
                                    </a>
                                ) : (
                                    <div className="text-xs text-slate-400 italic px-3 py-6 text-center">Preparing image…</div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <textarea
                    value={verifyNote}
                    onChange={(e) => setVerifyNote(e.target.value)}
                    placeholder="Optional verification note (visible to tutor)"
                    rows={2}
                    data-testid="admin-aadhaar-note"
                    className="w-full text-xs px-3 py-2 rounded-lg border border-amber-200 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                />
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => toggleVerify(true)}
                        disabled={verifyBusy || aadhaar.verified || !aadhaar.front_uploaded || !aadhaar.back_uploaded}
                        data-testid="admin-aadhaar-mark-verified"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {verifyBusy && <Loader2 className="w-3 h-3 animate-spin" />}
                        <ShieldCheck className="w-3.5 h-3.5" /> Mark Verified
                    </button>
                    <button
                        type="button"
                        onClick={() => toggleVerify(false)}
                        disabled={verifyBusy || !aadhaar.verified}
                        data-testid="admin-aadhaar-revoke"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-red-300 text-red-700 text-xs font-semibold hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ShieldAlert className="w-3.5 h-3.5" /> Revoke
                    </button>
                </div>
                {(!aadhaar.front_uploaded || !aadhaar.back_uploaded) && (
                    <div className="text-[11px] text-amber-800 inline-flex items-center gap-1" data-testid="admin-aadhaar-missing-warning">
                        <AlertTriangle className="w-3 h-3" /> Both sides must be uploaded before verification.
                    </div>
                )}
            </div>

            {/* Lifetime stats */}
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="admin-tutor-stats-block">
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600 mb-2 inline-flex items-center gap-1">
                    <Activity className="w-3.5 h-3.5" /> Activity
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <StatPill icon={Users} label="Applied" value={stats.applied_leads || 0} testid="admin-tutor-stat-applied" />
                    <StatPill icon={IndianRupee} label="Assignments" value={stats.assignments || 0} testid="admin-tutor-stat-assignments" />
                    <StatPill icon={Activity} label="Ongoing" value={stats.ongoing_tuitions || 0} testid="admin-tutor-stat-ongoing" />
                </div>
                {account ? (
                    <div className="mt-2 text-[11px] text-slate-600 grid sm:grid-cols-2 gap-1">
                        <div>Account: <span className="font-mono">{account.email}</span></div>
                        <div>Last login: {account.last_login_at ? new Date(account.last_login_at).toLocaleString() : "Never"}</div>
                        <div>First login: {account.first_login_at ? new Date(account.first_login_at).toLocaleString() : "Never"}</div>
                        <div>Must change pwd: {account.must_change_password ? "Yes" : "No"}</div>
                    </div>
                ) : (
                    <div className="mt-2 text-[11px] text-slate-500 italic" data-testid="admin-tutor-no-account">No tutor account provisioned yet.</div>
                )}
            </div>

            {/* Login history */}
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="admin-tutor-login-block">
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600 mb-2">Recent Logins</div>
                {history.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">No login activity recorded yet.</div>
                ) : (
                    <ul className="divide-y divide-slate-100 max-h-48 overflow-auto text-xs">
                        {history.map((h) => (
                            <li key={h.id} className="py-1.5 flex items-center justify-between gap-2" data-testid={`admin-tutor-login-row-${h.id}`}>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${h.success ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                                    {h.success ? "OK" : "FAIL"}
                                </span>
                                <span className="text-slate-500 font-mono shrink-0">{h.ip || "—"}</span>
                                <span className="text-slate-700 flex-1 truncate">{h.reason || (h.success ? "successful login" : "unknown")}</span>
                                <span className="text-slate-400 shrink-0">{new Date(h.at).toLocaleString()}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Manual referral note */}
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="admin-tutor-referral-block">
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600 mb-2">Refer &amp; Earn — Manual Note</div>
                <textarea
                    value={referralNote}
                    onChange={(e) => setReferralNote(e.target.value)}
                    rows={2}
                    placeholder="e.g. Referred Ramesh K. — credit ₹500 next payout"
                    data-testid="admin-tutor-referral-note"
                    className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] resize-none"
                />
                <div className="flex justify-end mt-2">
                    <button
                        type="button"
                        onClick={saveReferralNote}
                        disabled={savingReferral}
                        data-testid="admin-tutor-referral-save"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-semibold disabled:opacity-60"
                    >
                        {savingReferral ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        Save
                    </button>
                </div>
            </div>

            {/* V29 — Permanent Delete (super-admin + super-mode required) */}
            <AdminTutorPermanentDelete
                applicationId={applicationId}
                tutorName={data?.application?.name || data?.account?.name || "this tutor"}
                onDeleted={() => { try { onDeleted && onDeleted(); } catch { /* ignore */ } try { onClose && onClose(); } catch { /* ignore */ } }}
            />
        </div>
    );
}

function StatPill({ icon: Icon, label, value, testid }) {
    return (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center" data-testid={testid}>
            <Icon className="w-3.5 h-3.5 text-slate-500 mx-auto" />
            <div className="text-lg font-bold text-[#1E3A8A] leading-tight mt-0.5">{value}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        </div>
    );
}

// V30 — Surfaces the tutor's teaching service area in a way that reads cleanly
// regardless of mode. Live-Online-only tutors don't actually have PIN codes
// (the registration form hides the field), so we explicitly say
// "Online - All Locations" instead of an awkward empty list.
function ServiceAreaBlock({ application }) {
    const modeRaw = String(application.mode || "").toLowerCase();
    const isOnlineOnly = modeRaw.startsWith("live online") || modeRaw === "online";
    const isBoth = modeRaw.includes("both") || modeRaw.includes("home") && modeRaw.includes("online");
    const pins = Array.isArray(application.teaching_pin_codes) ? application.teaching_pin_codes : [];
    const homeCity = application.home_tuition_city || "";

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="admin-tutor-service-area-block">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600 mb-2">Service Area</div>
            <div className="text-[11px] text-slate-500 mb-2">
                Mode: <span className="font-semibold text-slate-800">{application.mode || "—"}</span>
                {!isOnlineOnly && homeCity && (
                    <>
                        {" · "}Home Tuition City:{" "}
                        <span
                            className="font-semibold text-slate-800 capitalize"
                            data-testid="admin-tutor-service-area-home-city"
                        >
                            {homeCity}
                        </span>
                    </>
                )}
            </div>
            {isOnlineOnly ? (
                <div
                    className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-900"
                    data-testid="admin-tutor-service-area-online"
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                    <strong>Online - All Locations</strong>
                </div>
            ) : pins.length === 0 ? (
                <div className="text-xs italic text-slate-400" data-testid="admin-tutor-service-area-empty">
                    No teaching PIN codes saved yet.
                </div>
            ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="admin-tutor-service-area-pins">
                    {isBoth && (
                        <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-full bg-violet-50 border border-violet-200 text-violet-800 mr-1">
                            Home tuition coverage + Online (anywhere)
                        </span>
                    )}
                    {pins.map((p) => (
                        <span
                            key={p}
                            className="font-mono text-xs px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900"
                            data-testid={`admin-tutor-service-area-pin-${p}`}
                        >
                            {p}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function revokeCachedAadhaarUrls(ref) {
    const cached = ref.current || {};
    if (cached.front) URL.revokeObjectURL(cached.front);
    if (cached.back) URL.revokeObjectURL(cached.back);
}

